import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { HiOutlineCheck } from "react-icons/hi2";
import { useFlightOfferUi } from "../../../../contexts/FlightOfferUiContext";
import { useSearch } from "../../../../contexts/SearchContext";
import { useBookingFlow } from "../hooks/useBookingFlow";
import {
  isAgentFlightResultsListUrl,
  logFlightsBackToCategoriesNavigation,
  scrollSearchOfferFlightFlowToTop,
} from "../../flightBookingPathUtils";
import { getVisibleFlightWizardSteps } from "./flightWizardConfig";

export default function FlightWizardStepStrip() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const routeParams = useParams();
  const { searchTab } = useSearch();
  const { currentStep, searchGate, goToStep } = useBookingFlow();
  const { setFlightSearchEditorOpen } = useFlightOfferUi();

  const onResultsListUrl = isAgentFlightResultsListUrl(
    routerLocation.pathname,
    routeParams.flightStep
  );
  /** סנכרון UI לעומת URL: ברשימת תוצאות ה־context עדיין יכול להיות search */
  const displayStep =
    onResultsListUrl && currentStep === "search" ? "results" : currentStep;

  const exitToAllProductCategories = useCallback(() => {
    const fromPath = routerLocation.pathname || "";
    const targetPath = "/p/dashboard/searchoffer";
    if (import.meta.env.DEV) {
      logFlightsBackToCategoriesNavigation({
        fromPath,
        targetPath,
        searchTabBefore: searchTab,
        searchTabAfter: searchTab,
        reason: "exit_all_categories_button",
      });
    }
    if (currentStep !== "search") {
      goToStep("search");
    }
    setFlightSearchEditorOpen(false);
    navigate(targetPath, { replace: true });
    requestAnimationFrame(() => scrollSearchOfferFlightFlowToTop());
  }, [
    currentStep,
    goToStep,
    navigate,
    routeParams,
    routerLocation.pathname,
    searchTab,
    setFlightSearchEditorOpen,
  ]);

  const visible = useMemo(() => getVisibleFlightWizardSteps(searchGate), [searchGate]);

  const currentVisibleIndex = useMemo(() => {
    const i = visible.findIndex((s) => s.id === displayStep);
    return i >= 0 ? i : 0;
  }, [visible, displayStep]);

  return (
    <div
      className="fw-strip-wrap fw-strip-wrap--toolbar"
      aria-label="שלבי ההזמנה וחזרה לקטגוריות מוצר"
    >
      <div className="fw-strip-scroll fw-strip-scroll--grow">
        <ol className="fw-strip">
          {visible.map((def, index) => {
            const Icon = def.Icon;
            const done = index < currentVisibleIndex;
            const current = index === currentVisibleIndex;
            const upcoming = index > currentVisibleIndex;
            const itemClass = [
              "fw-strip__item",
              done ? "fw-strip__item--done" : "",
              current ? "fw-strip__item--current" : "",
              upcoming ? "fw-strip__item--upcoming" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <li key={def.id} className={itemClass}>
                <button
                  type="button"
                  className="fw-strip__chip"
                  disabled={upcoming}
                  onClick={() => {
                    if (index >= currentVisibleIndex) return;
                    if (
                      def.id === "search" &&
                      (currentStep === "results" ||
                        (onResultsListUrl && currentStep === "search"))
                    ) {
                      setFlightSearchEditorOpen(true);
                      return;
                    }
                    goToStep(def.id);
                  }}
                  aria-current={current ? "step" : undefined}
                >
                  <span className="fw-strip__icon-wrap" aria-hidden>
                    {done ? (
                      <HiOutlineCheck className="fw-strip__check" strokeWidth={2.25} />
                    ) : (
                      <Icon className="fw-strip__icon" />
                    )}
                  </span>
                  <span className="fw-strip__text">
                    <span className="fw-strip__num">{index + 1}</span>
                    <span className="fw-strip__label">{def.shortTitle}</span>
                  </span>
                </button>
                {index < visible.length - 1 ? (
                  <span className="fw-strip__connector" aria-hidden />
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
      <button
        type="button"
        className="fw-exit-categories-btn"
        onClick={exitToAllProductCategories}
        title="חזרה לעמוד החיפוש עם בחירת סוג מוצר (חבילות, מאורגנים וכו׳)"
      >
        חזרה לכל הקטגוריות
      </button>
    </div>
  );
}
